import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiError, api } from "@/lib/api";
import { money } from "@/lib/format";
import { useAuth } from "@/state/auth";
import { Alert, Button, Card, Field } from "@/components/ui";

/**
 * Closing the account, from the owner's side.
 *
 * Deliberately plain about what it does, because it cannot be undone and because
 * "delete my account" means two different things to the person asking and to the
 * law. Personal details go; waybills and the wallet statement stay, since tax law
 * requires five years of records. Saying that before the button rather than after
 * is the whole point of the screen.
 */
interface Preview {
  can_close: boolean;
  balance_cents: number;
  open_shipments: number;
  what_happens: string[];
}

export function CloseAccount() {
  const { account, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");

  const { data: preview } = useQuery({
    queryKey: ["closure"],
    queryFn: () => api.get<Preview>("/v1/account/closure"),
    // Only asked once the person has opened the section: it is a question about
    // a destructive action, not something every settings visit needs to ask.
    enabled: open,
  });

  const expected = account?.trading_name || account?.legal_name || "";

  const close = useMutation({
    mutationFn: () => api.post("/v1/account/closure", { confirm }),
    // Signed out rather than left on a screen belonging to an account that no
    // longer exists.
    onSuccess: () => signOut(),
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Could not close the account."),
  });

  if (!open) {
    return (
      <Card>
        <h2 className="text-lg font-semibold tracking-tight">Close this account</h2>
        <p className="mt-1 text-sm text-ink-500">
          Erases your details and stops every login. Waybills and the wallet statement are kept as
          tax records.
        </p>
        <Button variant="secondary" className="mt-4" onClick={() => setOpen(true)}>
          Close this account
        </Button>
      </Card>
    );
  }

  return (
    <Card className="border-signal-bad/30">
      <h2 className="text-lg font-semibold tracking-tight">Close this account</h2>

      {preview && (
        <ul className="mt-3 space-y-1.5 text-sm text-ink-600">
          {preview.what_happens.map((line) => (
            <li key={line} className="flex gap-2">
              <span aria-hidden="true" className="text-ink-400">
                •
              </span>
              <span>{line}</span>
            </li>
          ))}
        </ul>
      )}

      {preview && !preview.can_close && (
        <div className="mt-4">
          <Alert tone="warn">
            {preview.balance_cents !== 0
              ? `This account still holds ${money(preview.balance_cents)}. Spend it or ask us to refund it first.`
              : `${preview.open_shipments} shipment${preview.open_shipments === 1 ? " is" : "s are"} still moving. The account can close once they are delivered or cancelled.`}
          </Alert>
        </div>
      )}

      {error && (
        <div className="mt-4">
          <Alert>{error}</Alert>
        </div>
      )}

      {preview?.can_close && (
        <Field
          label={`Type "${expected}" to confirm`}
          className="mt-4 max-w-sm"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          hint="Typed out in full, because this cannot be undone."
        />
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          loading={close.isPending}
          disabled={!preview?.can_close || confirm.trim().toLowerCase() !== expected.trim().toLowerCase()}
          onClick={() => close.mutate()}
        >
          Close it permanently
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            setOpen(false);
            setConfirm("");
            setError("");
          }}
        >
          Keep my account
        </Button>
      </div>
    </Card>
  );
}
